import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useGameStore } from "../../store/gameStore";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Send, ArrowLeft, Lock, MessageCircle, Check, CheckCheck } from "lucide-react";
import { RollBubble } from "./ChatPanel";

const conversationKey = (m) => [m.sender_member_id, m.whisper_target_member_id].sort().join("::");

export default function WhispersPanel({ gameId, isGm, myMemberId, members, initialOtherMemberId }) {
  const chatMessages = useGameStore((s) => s.chatMessages);
  const fetchChat = useGameStore((s) => s.fetchChat);
  const [activeKey, setActiveKey] = useState(initialOtherMemberId && myMemberId ? [myMemberId, initialOtherMemberId].sort().join("::") : null);
  const [newRecipient, setNewRecipient] = useState("");
  const [text, setText] = useState("");

  useEffect(() => {
    fetchChat(gameId);
  }, [gameId, fetchChat]);

  const memberName = (id) => members.find((m) => m.id === id)?.display_name || "Unknown";
  const otherMembers = members.filter((m) => m.id !== myMemberId);

  const whispers = useMemo(
    () =>
      chatMessages.filter(
        (m) =>
          (m.type === "whisper" || (m.type === "roll" && m.is_private)) &&
          (isGm || m.sender_member_id === myMemberId || m.whisper_target_member_id === myMemberId)
      ),
    [chatMessages, isGm, myMemberId]
  );

  const conversations = useMemo(() => {
    const map = new Map();
    for (const m of whispers) {
      const key = conversationKey(m);
      if (!map.has(key)) map.set(key, { key, sender_member_id: m.sender_member_id, whisper_target_member_id: m.whisper_target_member_id, messages: [] });
      map.get(key).messages.push(m);
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.messages[b.messages.length - 1].created_at) - new Date(a.messages[a.messages.length - 1].created_at)
    );
  }, [whispers]);

  const startNew = () => {
    if (!newRecipient) return;
    setActiveKey([myMemberId, newRecipient].sort().join("::"));
    setNewRecipient("");
  };

  const send = async () => {
    if (!text.trim() || !activeKey) return;
    const recipientId = activeKey.split("::").find((id) => id !== myMemberId);
    await api.post(`/games/${gameId}/chat/whisper`, { recipient_member_id: recipientId, content: text });
    setText("");
  };

  const activeConvo = useMemo(() => {
    if (!activeKey) return null;
    const existing = conversations.find((c) => c.key === activeKey);
    const [idA, idB] = activeKey.split("::");
    const convo = existing || { key: activeKey, sender_member_id: idA, whisper_target_member_id: idB, messages: [] };
    const iAmParticipant = convo.sender_member_id === myMemberId || convo.whisper_target_member_id === myMemberId;
    const otherPartyId = convo.sender_member_id === myMemberId ? convo.whisper_target_member_id : convo.sender_member_id;
    return { ...convo, iAmParticipant, otherPartyId };
  }, [activeKey, conversations, myMemberId]);

  // Mark the other party's messages to me as read the moment I open (or receive new messages in) this thread.
  // Only real whisper text messages carry read receipts — rolls don't need this API call.
  useEffect(() => {
    if (!activeConvo?.iAmParticipant) return;
    const hasUnread = activeConvo.messages.some((m) => m.type === "whisper" && m.whisper_target_member_id === myMemberId && !m.read_at);
    if (hasUnread) api.post(`/games/${gameId}/chat/whisper/read`, { other_member_id: activeConvo.otherPartyId });
  }, [activeConvo, gameId, myMemberId]);

  if (activeConvo) {
    const convo = activeConvo;
    const threadLabel = convo.iAmParticipant
      ? memberName(convo.otherPartyId)
      : `${memberName(convo.sender_member_id)} ↔ ${memberName(convo.whisper_target_member_id)}`;

    return (
      <div className="flex flex-col h-full" data-testid="whisper-thread">
        <div className="flex items-center gap-2 p-3 border-b border-white/10">
          <button data-testid="whisper-back-button" onClick={() => setActiveKey(null)} className="text-[#f1e0c5]/50 hover:text-[#D4AF37]">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Lock className="w-3.5 h-3.5 text-[#D4AF37]" />
          <span className="text-sm">{threadLabel}</span>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-2">
          {convo.messages.map((m) => (
            <div key={m.id} data-testid={`whisper-message-${m.id}`}>
              {m.type === "roll" ? (
                <>
                  <span className="text-[#D4AF37] text-xs font-semibold">{m.sender_name} rolls {m.content}</span>
                  <RollBubble msg={m} />
                </>
              ) : (
                <>
                  <span className="text-[#D4AF37] text-xs font-semibold">{m.sender_name}</span>
                  <p className="text-sm text-[#f1e0c5]/85 flex items-end gap-1.5">
                    {m.content}
                    {m.sender_member_id === myMemberId && (
                      <span data-testid={`whisper-read-status-${m.id}`} title={m.read_at ? "Seen" : "Sent"} className="shrink-0 pb-0.5">
                        {m.read_at ? <CheckCheck className="w-3 h-3 text-[#D4AF37]" /> : <Check className="w-3 h-3 text-[#f1e0c5]/30" />}
                      </span>
                    )}
                  </p>
                </>
              )}
            </div>
          ))}
          {convo.messages.length === 0 && <p className="text-xs text-[#f1e0c5]/30 text-center pt-10">No messages yet — say something in secret.</p>}
        </div>
        {convo.iAmParticipant && (
          <div className="flex gap-2 p-3 border-t border-white/10">
            <input
              data-testid="whisper-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={`Whisper to ${threadLabel}…`}
              className="flex-1 bg-black/30 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#D90429]"
            />
            <button data-testid="whisper-send-button" onClick={send} className="w-10 h-10 rounded-full bg-[#D90429] hover:bg-[#b8031f] flex items-center justify-center transition-colors duration-200">
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="whispers-panel">
      <div className="p-4 border-b border-white/10 space-y-2">
        <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50">Start a new whisper</p>
        <div className="flex gap-2">
          <Select value={newRecipient} onValueChange={setNewRecipient}>
            <SelectTrigger data-testid="whisper-new-recipient-select" className="bg-black/30 border-white/10">
              <SelectValue placeholder="Choose someone…" />
            </SelectTrigger>
            <SelectContent>
              {otherMembers.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`whisper-recipient-option-${m.id}`}>
                  {m.display_name} {m.role === "gm" ? "(GM)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button data-testid="whisper-start-button" onClick={startNew} disabled={!newRecipient} className="px-4 rounded-full bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-30 text-sm shrink-0 transition-colors duration-200">
            Whisper
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-2">
        {conversations.length === 0 && (
          <div className="text-xs text-[#f1e0c5]/30 text-center pt-10 flex flex-col items-center gap-2">
            <MessageCircle className="w-6 h-6 opacity-30" /> No secret conversations yet.
          </div>
        )}
        {conversations.map((c) => {
          const last = c.messages[c.messages.length - 1];
          const iAmParticipant = c.sender_member_id === myMemberId || c.whisper_target_member_id === myMemberId;
          const label = iAmParticipant
            ? memberName(c.sender_member_id === myMemberId ? c.whisper_target_member_id : c.sender_member_id)
            : `${memberName(c.sender_member_id)} ↔ ${memberName(c.whisper_target_member_id)}`;
          return (
            <button
              key={c.key}
              data-testid={`whisper-conversation-${c.key}`}
              onClick={() => setActiveKey(c.key)}
              className="w-full text-left flex items-center gap-2 bg-black/20 hover:bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 transition-colors duration-200"
            >
              <Lock className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{label}</p>
                <p className="text-xs text-[#f1e0c5]/40 truncate">{last?.content}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
